import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Header from '../../components/navigation/Header';


const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signUp, loading } = useAuth();

  const defaultTab = location?.state?.tab || 'signin';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [formData, setFormData] = useState({ fullName: '', email: '', password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authError, setAuthError] = useState('');

  const from = location?.state?.from?.pathname || '/user-dashboard';

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    setAuthError('');
  };

  const validateSignIn = () => {
    const newErrors = {};
    if (!formData?.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/?.test(formData?.email)) newErrors.email = 'Enter a valid email';
    if (!formData?.password) newErrors.password = 'Password is required';
    return newErrors;
  };

  const validateSignUp = () => {
    const newErrors = {};
    if (!formData?.fullName?.trim()) newErrors.fullName = 'Full name is required';
    if (!formData?.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/?.test(formData?.email)) newErrors.email = 'Enter a valid email';
    if (!formData?.password) newErrors.password = 'Password is required';
    else if (formData?.password?.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (formData?.password !== formData?.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    return newErrors;
  };

  const handleSignIn = async (e) => {
    e?.preventDefault();
    const validationErrors = validateSignIn();
    if (Object.keys(validationErrors)?.length > 0) { setErrors(validationErrors); return; }
    setIsSubmitting(true);
    setAuthError('');
    const { error } = await signIn(formData?.email, formData?.password);
    setIsSubmitting(false);
    if (error) {
      setAuthError(error?.message || 'Invalid email or password. Please try again.');
    } else {
      navigate(from, { replace: true });
    }
  };

  const handleSignUp = async (e) => {
    e?.preventDefault();
    const validationErrors = validateSignUp();
    if (Object.keys(validationErrors)?.length > 0) { setErrors(validationErrors); return; }
    setIsSubmitting(true);
    setAuthError('');
    const { error } = await signUp(formData?.email, formData?.password, formData?.fullName);
    setIsSubmitting(false);
    if (error) {
      setAuthError(error?.message || 'Could not create account. Please try again.');
    } else {
      navigate('/confirm-email', { replace: true });
    }
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setErrors({});
    setAuthError('');
    setFormData({ fullName: '', email: '', password: '', confirmPassword: '' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <Header
        user={null}
        onNavigate={(path) => navigate(path)}
        onPurchaseCredits={() => {}}
        onLogout={() => {}}
      />
      <div className="flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-600 to-blue-500 shadow-lg mb-4">
            <Icon name="Camera" size={28} color="white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Angle!</h1>
          <p className="text-gray-500 text-sm mt-1">Find your most attractive angle and see your popularity growing.</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-gray-100">
            <button
              onClick={() => switchTab('signin')}
              className={`flex-1 py-4 text-sm font-semibold transition-colors ${
                activeTab === 'signin' ?'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50' :'text-gray-500 hover:text-gray-700'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => switchTab('signup')}
              className={`flex-1 py-4 text-sm font-semibold transition-colors ${
                activeTab === 'signup' ?'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50' :'text-gray-500 hover:text-gray-700'
              }`}
            >
              Create Account
            </button>
          </div>

          <div className="p-8">
            <AnimatePresence mode="wait">
              {activeTab === 'signin' ? (
                <motion.form
                  key="signin"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.2 }}
                  onSubmit={handleSignIn}
                  className="space-y-5"
                >
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-1">Welcome back</h2>
                    <p className="text-sm text-gray-500">Sign in to access your credits and history</p>
                  </div>

                  {authError && (
                    <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <Icon name="AlertCircle" size={16} color="#ef4444" className="mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-red-600">{authError}</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData?.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className={`w-full px-4 py-3 rounded-xl border text-sm transition-colors outline-none focus:ring-2 focus:ring-purple-500/20 ${
                        errors?.email ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-purple-400'
                      }`}
                    />
                    {errors?.email && <p className="text-xs text-red-500 mt-1">{errors?.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                    <input
                      type="password"
                      name="password"
                      value={formData?.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      className={`w-full px-4 py-3 rounded-xl border text-sm transition-colors outline-none focus:ring-2 focus:ring-purple-500/20 ${
                        errors?.password ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-purple-400'
                      }`}
                    />
                    {errors?.password && <p className="text-xs text-red-500 mt-1">{errors?.password}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md"
                  >
                    {isSubmitting ? (
                      <><Icon name="Loader2" size={18} className="animate-spin" /> Signing in...</>
                    ) : 'Sign In'}
                  </button>

                  <div className="text-center space-y-2">
                    <p className="text-sm text-gray-500">
                      <button type="button" onClick={() => navigate('/forgot-password')} className="text-purple-600 font-medium hover:underline">
                        Forgot your password?
                      </button>
                    </p>
                    <p className="text-sm text-gray-500">
                      No account yet?{' '}
                      <button type="button" onClick={() => switchTab('signup')} className="text-purple-600 font-semibold hover:underline">
                        Create one free
                      </button>
                    </p>
                  </div>
                </motion.form>
              ) : (
                <motion.form
                  key="signup"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  onSubmit={handleSignUp}
                  className="space-y-5"
                >
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-1">Create your account</h2>
                    <p className="text-sm text-gray-500">Get 3 free credits to start ranking your photos</p>
                  </div>

                  {authError && (
                    <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <Icon name="AlertCircle" size={16} color="#ef4444" className="mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-red-600">{authError}</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData?.fullName}
                      onChange={handleChange}
                      placeholder="Your name"
                      className={`w-full px-4 py-3 rounded-xl border text-sm transition-colors outline-none focus:ring-2 focus:ring-purple-500/20 ${
                        errors?.fullName ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-purple-400'
                      }`}
                    />
                    {errors?.fullName && <p className="text-xs text-red-500 mt-1">{errors?.fullName}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData?.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className={`w-full px-4 py-3 rounded-xl border text-sm transition-colors outline-none focus:ring-2 focus:ring-purple-500/20 ${
                        errors?.email ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-purple-400'
                      }`}
                    />
                    {errors?.email && <p className="text-xs text-red-500 mt-1">{errors?.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                    <input
                      type="password"
                      name="password"
                      value={formData?.password}
                      onChange={handleChange}
                      placeholder="At least 6 characters"
                      className={`w-full px-4 py-3 rounded-xl border text-sm transition-colors outline-none focus:ring-2 focus:ring-purple-500/20 ${
                        errors?.password ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-purple-400'
                      }`}
                    />
                    {errors?.password && <p className="text-xs text-red-500 mt-1">{errors?.password}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData?.confirmPassword}
                      onChange={handleChange}
                      placeholder="Repeat your password"
                      className={`w-full px-4 py-3 rounded-xl border text-sm transition-colors outline-none focus:ring-2 focus:ring-purple-500/20 ${
                        errors?.confirmPassword ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-purple-400'
                      }`}
                    />
                    {errors?.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors?.confirmPassword}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md"
                  >
                    {isSubmitting ? (
                      <><Icon name="Loader2" size={18} className="animate-spin" /> Creating account...</>
                    ) : 'Create Account — It\'s Free'}
                  </button>

                  <div className="text-center">
                    <p className="text-sm text-gray-500">
                      Already have an account?{' '}
                      <button type="button" onClick={() => switchTab('signin')} className="text-purple-600 font-semibold hover:underline">
                        Sign in
                      </button>
                    </p>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          By continuing, you agree to our{' '}
          <a href="/terms-of-service" className="underline hover:text-gray-200 transition-colors">Terms of Service</a>
          {' '}and{' '}
          <a href="/privacy-policy" className="underline hover:text-gray-200 transition-colors">Privacy Policy</a>.
        </p>
      </div>
      </div>
    </div>
  );
};

export default AuthPage;
